/**
 * Pure utility functions for map operations
 * These functions have NO dependencies on Vue component context
 */

// Create stretch renderer for imagery layers
export const createStretchRenderer = (min, max, fromColor, toColor) => {
    return {
      stretchType: "min-max",
      colorRamp: {
        type: "algorithmic",
        fromColor: fromColor,
        toColor: toColor,
      },
      customStatistics: [
        {
          min: min,
          max: max,
        },
      ],
    };
  };
  
  // Create stop symbol for directions
  export const createStopSymbol = (outline, fill, size) => {
    return {
      style: "cross",
      size: size,
      outline: {
        width: "2px",
        color: outline,
      },
      color: fill,
    };
  };
  
  // Calculate statistics from an array of values
  export const calculateStats = (values) => {
    if (!values || !values.length) {
      return { average: "N/A", min: "N/A", max: "N/A", stdDev: "N/A" };
    }
    
    const average = values.reduce((a, b) => a + b, 0) / values.length;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const variance = values.reduce((a, b) => a + Math.pow(b - average, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);
    
    return {
      average: parseFloat(average).toFixed(2),
      min: parseFloat(min).toFixed(2),
      max: parseFloat(max).toFixed(2),
      stdDev: parseFloat(stdDev).toFixed(2),
    };
  };
  
  // Generate sample points within a polygon
  export const generateSamplePoints = (geometryEngine, polygon, count) => {
    if (!polygon || !polygon.extent) {
      return [];
    }
    
    const extent = polygon.extent;
    const points = [];
    const gridSize = Math.ceil(Math.sqrt(count));
    const stepX = (extent.xmax - extent.xmin) / gridSize;
    const stepY = (extent.ymax - extent.ymin) / gridSize;
  
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const x = extent.xmin + (i + 0.5) * stepX;
        const y = extent.ymin + (j + 0.5) * stepY;
        const point = {
          type: "point",
          x,
          y,
          spatialReference: polygon.spatialReference,
        };
        
        if (geometryEngine && geometryEngine.contains(polygon, point)) {
          points.push(point);
        }
        
        if (points.length >= count) break;
      }
      if (points.length >= count) break;
    }
  
    return points;
  };
  
  // Get date range for predefined filters
  export const getBiocharDateRange = (filterType) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let start, end;
  
    switch (filterType) {
      case "today":
        start = new Date(today);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
        break;
      case "yesterday":
        start = new Date(today);
        start.setDate(today.getDate() - 1);
        end = new Date(start);
        end.setHours(23, 59, 59, 999);
        break;
      case "thisweek":
        start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
        break;
      case "lastweek":
        start = new Date(today);
        start.setDate(today.getDate() - today.getDay() - 7);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case "thismonth":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case "lastmonth":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        end.setHours(23, 59, 59, 999);
        break;
      case "thisyear":
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        end.setHours(23, 59, 59, 999);
        break;
      case "lastyear":
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        start = null;
        end = null;
    }
    
    return { start, end };
  };
  
  // Format date for SQL queries
  export const formatDateForQuery = (dateString) => {
    if (!dateString) return null;
    
    if (dateString.includes("T")) {
      return dateString;
    } else {
      const date = new Date(dateString + "T00:00:00");
      const utcDate = new Date(date.getTime() - 1 * 60 * 60 * 1000);
      return utcDate.toISOString();
    }
  };
  
  // Safe string escape for SQL queries
  export const escapeSqlString = (str) => {
    if (!str) return "''";
    return `'${str.replace(/'/g, "''")}'`;
  };
  
  // Calculate buffer area in different units
  export const calculateBufferArea = (geometryEngine, buffer, unit = "hectares") => {
    if (!geometryEngine || !buffer) return 0;
    
    try {
      return geometryEngine.geodesicArea(buffer, unit);
    } catch (error) {
      console.error("Error calculating buffer area:", error);
      return 0;
    }
  };